"use client";

import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { cn } from "@/lib/shared/utils";
import { NavigationItem } from "@/types/menu";
import { IconChevronRight } from "@tabler/icons-react";
import Link from "next/link";
import React, { useEffect, useState } from "react";

interface AnimatedMenuItemProps {
  item: NavigationItem;
  depth?: number;
  pathname: string;
  onItemClick: (item: NavigationItem) => void;
  index?: number;
}

export function AnimatedMenuItem({
  item,
  depth = 0,
  pathname,
  onItemClick,
  index = 0,
}: AnimatedMenuItemProps) {
  const [itemHovered, setItemHovered] = useState(false);

  if (item.type === "divider") {
    return <div key={item.id} className="my-2 border-t border-border" />;
  }

  if (item.type === "header") {
    return (
      <div key={item.id} className="px-3 pt-4 pb-1 first:pt-0">
        <div className="text-[11px] font-semibold text-muted-foreground/70 uppercase tracking-wider">
          {item.title}
        </div>
      </div>
    );
  }

  // Check if this item or any of its children are active
  const isDirectlyActive = item.url !== "#" && (
    pathname === item.url || 
    (item.url !== "/" && pathname.startsWith(item.url + "/"))
  );
  
  // Check if any children are active (for parent menu items)
  const isChildActive = item.children?.some(child => 
    child.url !== "#" && (
      pathname === child.url || 
      (child.url !== "/" && pathname.startsWith(child.url + "/"))
    )
  ) || false;
  
  const isActive = isDirectlyActive || isChildActive;
  const hasChildren = item.children && item.children.length > 0;
  const isDisabled = item.disabled === true;

  return (
    <SidebarMenuItem key={item.id}>
      <SidebarMenuButton
        asChild={item.url !== "#" && !isDisabled}
        tooltip={isDisabled ? `${item.title} (Coming Soon)` : item.title}
        className={cn(
          "transition-all duration-200 ease-out",
          depth > 0 && "ml-4 text-sm",
          isActive && "bg-accent text-accent-foreground",
          itemHovered && !isDisabled && "bg-accent/50",
          isDisabled 
            ? "cursor-not-allowed opacity-50 text-muted-foreground pointer-events-none" 
            : "cursor-pointer"
        )}
        onMouseEnter={() => !isDisabled && setItemHovered(true)}
        onMouseLeave={() => !isDisabled && setItemHovered(false)}
        onClick={() =>
          !isDisabled && (!item.url || item.url === "#") ? onItemClick(item) : undefined
        }
      >
        {item.url !== "#" && !isDisabled ? (
          <Link href={item.url} className="flex items-center w-full">
            {item.icon && (
              <item.icon
                className={cn(
                  "transition-all duration-200 flex-shrink-0",
                  depth > 0 ? "h-3 w-3" : "h-4 w-4",
                  itemHovered && "text-primary scale-110"
                )}
              />
            )}
            <span
              className={cn(
                "flex-1 transition-all duration-200 group-data-[collapsible=icon]:hidden"
              )}
            >
              {item.title}
            </span>
            {hasChildren && (
              <IconChevronRight
                className={cn(
                  "h-4 w-4 ml-auto transition-all duration-200 group-data-[collapsible=icon]:hidden",
                  itemHovered && "text-primary transform rotate-90"
                )}
              />
            )}
          </Link>
        ) : (
          <div className="flex items-center w-full">
            {item.icon && (
              <item.icon
                className={cn(
                  "transition-all duration-200 flex-shrink-0",
                  depth > 0 ? "h-3 w-3" : "h-4 w-4",
                  itemHovered && !isDisabled && "text-primary scale-110"
                )}
              />
            )}
            <span
              className={cn(
                "flex-1 transition-all duration-200 group-data-[collapsible=icon]:hidden ml-2"
              )}
            >
              {item.title}
            </span>
            {hasChildren && (
              <IconChevronRight
                className={cn(
                  "h-4 w-4 ml-auto transition-all duration-200 group-data-[collapsible=icon]:hidden",
                  itemHovered && !isDisabled && "text-primary transform rotate-90"
                )}
              />
            )}
          </div>
        )}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

// Loading skeleton component
export function AnimatedMenuSkeleton({ count = 3 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <SidebarMenuItem key={`skeleton-${index}`}>
          <div className="flex items-center gap-2 p-2">
            <div className="h-4 w-4 bg-muted rounded animate-pulse" />
            <div className="h-4 flex-1 bg-muted rounded animate-pulse" />
          </div>
        </SidebarMenuItem>
      ))}
    </>
  );
}
